import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Identifier, RaRecord, useDataProvider, useGetIdentity, useNotify } from 'react-admin';
import { SelectChangeEvent } from '@mui/material';
import { TPADataType } from '../utils/DataTypes';
import {
  getStartEndDate,
  handleReportTypeChange as handleReportTypeChangeHandler,
  handleWeekChange as handleWeekChangeHandler,
  handleYearChange as handleYearChangeHandler,
  handleStartDateChange as handleStartDateChangeHandler,
  handleEndDateChange as handleEndDateChangeHandler,
  mapReportTypeToClientSource,
  isDateInFuture,
  isPaDateTooFarInFuture,
  calculateStartEndFromBilling,
  calculateBillingDateString,
  calculateStartEndFromBillingAssist,
  getMasterStartEndDate,
} from './reportHandlers';
import {
  reportTypes,
  TOAST_MESSAGES,
  FORM_LABELS,
  BUTTONS,
  DIALOG,
  REPORT_TYPES,
  REPORT_STATUS,
  DEFAULTS,
  GENERATE_REPORTS_VALUES,
} from '../constants';
import { toast } from 'react-toastify';

interface ReportFormContextType {
  // State
  startDate: string;
  setStartDate: (val: string) => void;
  endDate: string;
  setEndDate: (val: string) => void;
  selectedClients: RaRecord<Identifier>[];
  setClients: (val: RaRecord<Identifier>[]) => void;
  reportType: string;
  setReportType: (val: string) => void;
  resetClients: boolean;
  setResetClients: (val: boolean) => void;
  showWeekDropdown: boolean;
  setShowWeekDropdown: (val: boolean) => void;
  availableWeeks: string[];
  setAvailableWeeks: (val: string[]) => void;
  selectedYear: number;
  setSelectedYear: (val: number) => void;
  selectedMonth: string;
  setSelectedMonth: (val: string) => void;
  selectedWeek: string;
  setSelectedWeek: (val: string) => void;
  disableDateFields: boolean;
  setDisabledFields: (val: boolean) => void;
  billingDate: string;
  setBillingDate: (val: string) => void;
  isStartDateInvalid: boolean;
  setIsStartDateInvalid: (val: boolean) => void;
  isEndDateInvalid: boolean;
  setIsEndDateInvalid: (val: boolean) => void;
  monthYearDisabled: boolean;
  setMonthYearDisabled: (val: boolean) => void;
  filterType: string;
  setFilterType: (val: string) => void;
  selectedTpas: TPADataType[];
  setSelectedTpas: (val: TPADataType[]) => void;
  isMedSubReport: boolean;
  setIsMedSubReport: (val: boolean) => void;
  latestClaimDate: string | null;

  // Dialogs
  openDialog: boolean;
  setOpenDialog: (val: boolean) => void;
  openTpaDialog: boolean;
  setOpenTpaDialog: (val: boolean) => void;
  openConfirmCreateDialog: boolean;
  setOpenConfirmCreateDialog: (val: boolean) => void;
  openMasterDialog: boolean;
  setOpenMasterDialog: (val: boolean) => void;
  handleMasterSubmit: (masterDates: { start: string; end: string; month: string; year: number }) => Promise<void>;

  // Handlers
  handleGenerateReport: () => void;
  handleConfirmCreateReport: () => void;
  handleTpaSelection: (values: RaRecord<Identifier>[]) => void;
  handleReportTypeChange: (e: SelectChangeEvent<string>) => void;
  handleStartDateChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleEndDateChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleWeekChange: (e: SelectChangeEvent<string>) => void;
  handleYearChange: (e: SelectChangeEvent<string>) => void;
  confirmGenerateForAll: (setOpenDialog: (value: boolean) => void, submitReport: (clients: any) => void) => void;
  submitReport: (clients: any) => void;

  // Utilities
  mapReportTypeToClientSource: (reportType: string) => string;
  months: string[];
  reportTypes: { value: string; label: string }[];
  isButtonDisabled: boolean;
  notify: (message: string, options?: any) => void;
}

const ReportFormContext = createContext<ReportFormContextType | undefined>(undefined);

export const ReportFormProvider = ({ children }: { children: ReactNode }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedClients, setClients] = useState<RaRecord<Identifier>[]>([]);
  const [reportType, setReportType] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openTpaDialog, setOpenTpaDialog] = useState(false);
  const [resetClients, setResetClients] = useState(false);
  const [showWeekDropdown, setShowWeekDropdown] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState(['']);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [disableDateFields, setDisabledFields] = useState(false);
  const [billingDate, setBillingDate] = useState('');
  const [openConfirmCreateDialog, setOpenConfirmCreateDialog] = useState(false);
  const [paReportExists, setPaReportExists] = useState<boolean | null>(null);
  const [isStartDateInvalid, setIsStartDateInvalid] = useState(false);
  const [isEndDateInvalid, setIsEndDateInvalid] = useState(false);
  const [monthYearDisabled, setMonthYearDisabled] = useState(false);
  const [filterType, setFilterType] = useState('client');
  const [selectedTpas, setSelectedTpas] = useState<TPADataType[]>([]);
  const [isMedSubReport, setIsMedSubReport] = useState(false);
  const [latestClaimDate, setLatestClaimDate] = useState<string | null>(null);
  const [openMasterDialog, setOpenMasterDialog] = useState(false);

  const { identity } = useGetIdentity();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const months = Array.from({ length: 12 }, (_, i) => new Date(2000, i, 1).toLocaleString('en-US', { month: 'long' }));

  const isStandardButtonDisabled =
    !startDate || !endDate || startDate >= endDate || !reportType || isStartDateInvalid || isEndDateInvalid;

  const isMedSubButtonDisabled = !reportType;

  const isButtonDisabled = isMedSubReport ? isMedSubButtonDisabled : isStandardButtonDisabled;

  useEffect(() => {
    if (!isMedSubReport) {
      const isPaOrAssistType =
        reportType === REPORT_TYPES.PA_REVIEW ||
        reportType === REPORT_TYPES.PA_CLIENT_BILL ||
        reportType === REPORT_TYPES.ASSIST_REVIEW ||
        reportType === REPORT_TYPES.ASSIST_CLIENT_BILL;
      const isWeekMonthYearType = showWeekDropdown;

      if (isPaOrAssistType) {
        if (selectedMonth && selectedYear) {
          if (isPaDateTooFarInFuture(selectedYear, selectedMonth)) {
            const isAssist =
              reportType === REPORT_TYPES.ASSIST_REVIEW || reportType === REPORT_TYPES.ASSIST_CLIENT_BILL;

            const notificationKey = isAssist
              ? 'notifications.ASSIST_DATE_IN_FUTURE'
              : 'notifications.PA_DATE_IN_FUTURE';

            notify(notificationKey, { type: 'error' });

            setSelectedMonth('');
            setStartDate('');
            setEndDate('');
            return;
          }

          const calculatedBillingDate = calculateBillingDateString(selectedYear, selectedMonth);
          setBillingDate(calculatedBillingDate);

          let start = '';
          let end = '';

          if (reportType === REPORT_TYPES.ASSIST_REVIEW || reportType === REPORT_TYPES.ASSIST_CLIENT_BILL) {
            ({ start, end } = calculateStartEndFromBillingAssist(calculatedBillingDate));
          } else {
            ({ start, end } = calculateStartEndFromBilling(calculatedBillingDate));
          }

          setStartDate(start);
          setEndDate(end);
        } else {
          setBillingDate('');
          setStartDate('');
          setEndDate('');
        }
      } else if (isWeekMonthYearType && selectedWeek !== GENERATE_REPORTS_VALUES.CUSTOM_DATES) {
        if (selectedMonth && selectedYear) {
          let start = '';
          let end = '';
          if (selectedWeek) {
            ({ start, end } = getStartEndDate(reportType, selectedWeek, selectedMonth, selectedYear));
          } else {
            const monthIndex = months.indexOf(selectedMonth);
            if (monthIndex !== -1) {
              const daysInMonth = new Date(selectedYear, monthIndex + 1, 0).getDate();
              start = new Date(selectedYear, monthIndex, 1).toLocaleDateString('en-CA');
              end = new Date(selectedYear, monthIndex, daysInMonth).toLocaleDateString('en-CA');
            }
          }

          setStartDate(start);
          setEndDate(end);

          if (end && isDateInFuture(end)) {
            notify('notifications.DATE_ENDS_IN_FUTURE', { type: 'error' });
            setIsStartDateInvalid(true);
            setIsEndDateInvalid(true);
            return;
          }
          setBillingDate('');
          setIsStartDateInvalid(false);
          setIsEndDateInvalid(false);
        } else {
          setStartDate('');
          setEndDate('');
          setBillingDate('');
        }
      } else {
        setBillingDate('');
      }
    } else {
      setStartDate('');
      setEndDate('');
      setSelectedWeek('');
      setSelectedMonth('');
      setBillingDate('');
    }
  }, [reportType, selectedMonth, selectedYear, selectedWeek, showWeekDropdown, notify, months, isMedSubReport]);

  useEffect(() => {
    const fetchLatestClaimDate = async () => {
      if (!identity) return;
      try {
        const res = await dataProvider.getOne('LatestClaimDate', { id: 'latest' });
        setLatestClaimDate(res?.data?.date || null);
      } catch (e) {
        setLatestClaimDate(null);
      }
    };
    fetchLatestClaimDate();
  }, [dataProvider, identity]);

  useEffect(() => {
    if (filterType === 'client') {
      setSelectedTpas([]);
    } else {
      setClients([]);
    }
  }, [filterType]);

  const getBillingExistsResource = (rt: string) => {
    if (rt === REPORT_TYPES.PA_REVIEW || rt === REPORT_TYPES.PA_CLIENT_BILL) return 'CheckPABillingExists';
    return null;
  };

  const submitReport = async (
    clients: any,
    manualReportType?: string,
    silent = false,
    overrideStart?: string,
    overrideEnd?: string,
    overrideWeek?: string,
    overrideBilling?: string,
    forcedFilterType?: string
  ) => {
    const currentReportType = manualReportType || reportType;

    const activeFilterType = forcedFilterType || filterType;
    const isTpaMode = activeFilterType === 'tpa';

    const isAllClients =
      !isTpaMode && clients.length === 1 && clients[0]?.client_name === GENERATE_REPORTS_VALUES.ALL_CLIENTS;
    const clientNames = isTpaMode
      ? []
      : isAllClients
      ? [GENERATE_REPORTS_VALUES.ALL_CLIENTS]
      : clients.map((c: any) => c.client_name);
    const clientNumbers = isTpaMode ? [] : isAllClients ? [] : clients.map((c: any) => c.client_number);

    const isAllTpas = isTpaMode && selectedTpas.length === 0;
    const tpaNames = !isTpaMode ? [] : isAllTpas ? ['All TPAs'] : selectedTpas.map((t: any) => t.vendor_name);
    const tpaNumbers = !isTpaMode ? [] : isAllTpas ? [] : selectedTpas.map((t: any) => t.vendor_number);

    const needsBillingDate =
      currentReportType === REPORT_TYPES.PA_REVIEW ||
      currentReportType === REPORT_TYPES.PA_CLIENT_BILL ||
      currentReportType === REPORT_TYPES.ASSIST_REVIEW ||
      currentReportType === REPORT_TYPES.ASSIST_CLIENT_BILL;

    const usedWeekDropdown =
      !needsBillingDate &&
      (currentReportType === REPORT_TYPES.FULL_CLAIMS ||
        currentReportType === REPORT_TYPES.GP_SALES_ORDER ||
        currentReportType === REPORT_TYPES.INVOICE_DETAIL ||
        currentReportType === REPORT_TYPES.POS_REBATE ||
        currentReportType === REPORT_TYPES.MEMBER_SUMMARY ||
        currentReportType === REPORT_TYPES.PA_STATUS_REPORT);

    let finalReportType = currentReportType;
    if (isMedSubReport) {
      finalReportType = `${currentReportType} Med-Sub`;
    }
    const today = new Date().toLocaleDateString('en-CA');
    const finalStartDate = overrideStart || (isMedSubReport ? '1900-01-01' : startDate);
    const finalEndDate = overrideEnd || (isMedSubReport ? today : endDate);
    const finalBillingDate = needsBillingDate ? overrideBilling || billingDate : isMedSubReport ? today : '';
    const finalWeek = usedWeekDropdown ? overrideWeek || selectedWeek : '';

    const payload = {
      filters: {
        start_date: finalStartDate,
        end_date: finalEndDate,
        clients: clientNames,
        client_numbers: clientNumbers,
        tpas: tpaNames,
        tpa_numbers: tpaNumbers,
        is_tpa: activeFilterType === 'tpa' ? 'Y' : 'N',
        report_type: finalReportType,
        week: finalWeek,
        billing_date: finalBillingDate,
        pa_report_exists:
          currentReportType === REPORT_TYPES.PA_REVIEW || currentReportType === REPORT_TYPES.PA_CLIENT_BILL
            ? paReportExists
            : null,
        semi_monthly_recon_query: '',
        semi_monthly_recon_query_params: {
          account_name: '',
          account_number: '',
          transaction_status: '',
          claim_id: '',
          variance_filter: '',
          file_date: '',
          date_filled_start: '',
          date_filled_end: '',
        },
        is_med_sub_report: isMedSubReport,
      },
      requested_by: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      report_status: REPORT_STATUS.STARTED,
    };

    const usesSpecialFilters = (type: string) =>
      type === REPORT_TYPES.MEMBER_SUMMARY ||
      type === REPORT_TYPES.PA_STATUS_REPORT ||
      type === REPORT_TYPES.INVOICE_DETAIL;

    try {
      const response = await dataProvider.create('reports', {
        data: payload,
      });
      if (!silent) {
        toast.success(TOAST_MESSAGES.REPORT_SUBMITTED);
        setStartDate('');
        setEndDate('');
        setReportType('');
        setSelectedWeek('');
        setSelectedMonth('');
        setBillingDate('');
        setSelectedYear(new Date().getFullYear());
        setShowWeekDropdown(false);
        setDisabledFields(false);
        // setResetClients(true);
        setIsMedSubReport(false);
        // setTimeout(() => setResetClients(false), 500);
        if (usesSpecialFilters(currentReportType)) {
          setClients([]);
          setSelectedTpas([]);
          setResetClients(true);
          setTimeout(() => setResetClients(false), 500);
        }
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.REPORT_ERROR);
    }

    return Promise.resolve();
  };

  const handleGenerateReport = async () => {
    setPaReportExists(null);

    if (!isMedSubReport && latestClaimDate && endDate) {
      const selectedEnd = new Date(endDate);
      const latestClaim = new Date(latestClaimDate);

      if (selectedEnd > latestClaim) {
        notify('Selected end date is later than the latest available claim date.', {
          type: 'warning',
        });
      }
    }
    const isPaTypeOnly = reportType === REPORT_TYPES.PA_REVIEW || reportType === REPORT_TYPES.PA_CLIENT_BILL;

    if (!isPaTypeOnly && (isDateInFuture(startDate) || isDateInFuture(endDate))) {
      notify('notifications.START_OR_END_DATE_IN_FUTURE', { type: 'error' });
      return;
    }

    if (isPaTypeOnly) {
      try {
        const resource = getBillingExistsResource(reportType);
        if (!resource) {
          notify('notifications.ERROR_CHECKING_PA_REPORT_EXISTS', { type: 'error' });
          return;
        }
        const response = await dataProvider.getOne(resource as string, { id: billingDate });
        const existsResult = response?.data?.exists;
        setPaReportExists(existsResult);
        if (!existsResult) {
          if (reportType === REPORT_TYPES.PA_REVIEW) {
            setOpenConfirmCreateDialog(true);
          } else if (reportType === REPORT_TYPES.PA_CLIENT_BILL) {
            notify('notifications.CHECK_PA_REVIEW_EXISTS' as any, { type: 'info' });
          }
          return;
        }
      } catch (error: any) {
        notify('notifications.ERROR_CHECKING_PA_REPORT_EXISTS', { type: 'error' });
        return;
      }
    }

    if (filterType === 'tpa') {
      if (selectedTpas.length === 0) {
        setOpenTpaDialog(true);
        return;
      }
    } else {
      if (selectedClients.length === 0) {
        setOpenDialog(true);
        return;
      }
    }
    submitReport(selectedClients);
  };

  const handleMasterSubmit = async (masterDates: any) => {
    const { week, month, year, start, end } = masterDates;
    const allClientsPayload = [{ client_name: GENERATE_REPORTS_VALUES.ALL_CLIENTS }];
    const calculatedBillingDate = calculateBillingDateString(year, month);

    let paReportsSkipped = false;

    const standardWeekly = [
      REPORT_TYPES.FULL_CLAIMS,
      REPORT_TYPES.GP_SALES_ORDER,
      REPORT_TYPES.INVOICE_DETAIL,
      REPORT_TYPES.MEMBER_SUMMARY,
      REPORT_TYPES.PA_STATUS_REPORT,
    ];

    let reportsToRun = [...standardWeekly];

    if (week === 'Week 2' || week === 'Week 4') {
      reportsToRun.splice(1, 0, REPORT_TYPES.POS_REBATE);
    }

    if (week === 'Week 4') {
      const assistReports = [REPORT_TYPES.ASSIST_REVIEW, REPORT_TYPES.ASSIST_CLIENT_BILL];

      const paResponse = await dataProvider.getOne('CheckPABillingExists', { id: calculatedBillingDate });
      const paDataExists = paResponse?.data?.exists;

      if (paDataExists) {
        const paReports = [REPORT_TYPES.PA_REVIEW, REPORT_TYPES.PA_CLIENT_BILL];
        reportsToRun.splice(5, 0, ...paReports, ...assistReports);
      } else {
        paReportsSkipped = true;
        reportsToRun.splice(5, 0, ...assistReports);
      }
    }

    setOpenMasterDialog(false);

    const dualModeReports = [REPORT_TYPES.MEMBER_SUMMARY, REPORT_TYPES.PA_STATUS_REPORT];
    const extraTpaRuns = reportsToRun.filter((type) => dualModeReports.includes(type)).length;
    const totalSubmissions = reportsToRun.length + extraTpaRuns;

    try {
      await Promise.all(
        reportsToRun.map((type) => {
          const { start: reportStart, end: reportEnd } = getMasterStartEndDate(type, week, month, year);

          const clientPromise = submitReport(
            allClientsPayload,
            type,
            true,
            reportStart,
            reportEnd,
            week,
            calculatedBillingDate,
            'client'
          );

          if (dualModeReports.includes(type)) {
            const tpaPromise = submitReport([], type, true, reportStart, reportEnd, week, calculatedBillingDate, 'tpa');
            return [clientPromise, tpaPromise];
          }
          return [clientPromise];
        })
      );
      if (paReportsSkipped) {
        toast.warn(`Master Run complete: ${totalSubmissions} reports submitted (PA reports skipped).`);
      } else {
        toast.success(`Master Run: ${totalSubmissions} reports submitted successfully`);
      }
    } catch (error) {
      toast.error('Master run partially failed.');
    }
  };

  const handleConfirmCreateReport = () => {
    setOpenConfirmCreateDialog(false);
    if (selectedClients.length === 0) {
      setOpenDialog(true);
      return;
    }
    submitReport(selectedClients);
  };

  const handleTpaSelection = (values: RaRecord<Identifier>[]) => {
    const tpas = values.map((record) => ({
      ...record,
      id: String(record.id),
    }));
    setSelectedTpas(tpas as TPADataType[]);
  };

  const confirmGenerateForAll = (setOpenDialog: (value: boolean) => void, submitReport: (clients: any) => void) => {
    setOpenDialog(false);
    submitReport([{ client_name: GENERATE_REPORTS_VALUES.ALL_CLIENTS }]);
  };

  const handleReportTypeChange = (e: SelectChangeEvent<string>) => {
    handleReportTypeChangeHandler(
      e,
      setReportType,
      setAvailableWeeks,
      setShowWeekDropdown,
      setDisabledFields,
      setSelectedWeek,
      setSelectedMonth,
      setSelectedYear,
      setStartDate,
      setEndDate,
      setBillingDate,
      setIsStartDateInvalid,
      setIsEndDateInvalid,
      setClients,
      setSelectedTpas,
      setFilterType,
      setIsMedSubReport,
      reportType
    );
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleStartDateChangeHandler(e, setStartDate, endDate, notify, setIsStartDateInvalid, setIsEndDateInvalid);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    handleEndDateChangeHandler(
      e,
      setEndDate,
      startDate,
      latestClaimDate,
      notify,
      setIsEndDateInvalid,
      setIsStartDateInvalid
    );
  };

  const handleWeekChange = (e: SelectChangeEvent<string>) => {
    handleWeekChangeHandler(
      e,
      setSelectedWeek,
      setDisabledFields,
      setMonthYearDisabled,
      setStartDate,
      setEndDate,
      setIsStartDateInvalid,
      setIsEndDateInvalid,
      setSelectedMonth,
      setSelectedYear
    );
  };

  const handleYearChange = (e: SelectChangeEvent<string>) => {
    handleYearChangeHandler(e, setSelectedYear);
  };

  const value = {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    selectedClients,
    setClients,
    reportType,
    setReportType,
    resetClients,
    setResetClients,
    showWeekDropdown,
    setShowWeekDropdown,
    availableWeeks,
    setAvailableWeeks,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedWeek,
    setSelectedWeek,
    disableDateFields,
    setDisabledFields,
    billingDate,
    setBillingDate,
    isStartDateInvalid,
    setIsStartDateInvalid,
    isEndDateInvalid,
    setIsEndDateInvalid,
    monthYearDisabled,
    setMonthYearDisabled,
    filterType,
    setFilterType,
    selectedTpas,
    setSelectedTpas,
    openDialog,
    setOpenDialog,
    openTpaDialog,
    setOpenTpaDialog,
    openConfirmCreateDialog,
    setOpenConfirmCreateDialog,
    handleGenerateReport,
    handleConfirmCreateReport,
    handleTpaSelection,
    handleReportTypeChange,
    handleStartDateChange,
    handleEndDateChange,
    handleWeekChange,
    handleYearChange,
    confirmGenerateForAll,
    submitReport,
    mapReportTypeToClientSource,
    months,
    reportTypes,
    isButtonDisabled,
    notify,
    isMedSubReport,
    setIsMedSubReport,
    latestClaimDate,
    openMasterDialog,
    setOpenMasterDialog,
    handleMasterSubmit,
  };

  return <ReportFormContext.Provider value={value}>{children}</ReportFormContext.Provider>;
};

export const useReportForm = () => {
  const context = useContext(ReportFormContext);
  if (!context) {
    throw new Error('useReportForm must be used within a ReportFormProvider');
  }
  return context;
};
